import React from 'react';
import {
    ACCESS_FEATURES,
    REQUIRED_FEATURE_KEYS,
    VDR_PRESETS,
    aclIdsForDraft,
    createEmptyAccessDraft,
    draftFromGroup,
    normalizeFeatureKeys,
} from '../NewProjectFlow/accessGroupCatalog';
import styles from './css/index.module.css';

function Check({ checked, muted }) {
    const mark = checked ? (muted ? styles.boxMuted : styles.boxOn) : '';
    return <span className={`${styles.box} ${mark}`}>{checked ? <span className={styles.tick} /> : null}</span>;
}

function Radio({ checked }) {
    return <span className={`${styles.radio} ${checked ? styles.radioOn : ''}`}>{checked ? <span className={styles.dot} /> : null}</span>;
}

const GRANTS = [
    {
        key: 'intelligence',
        title: 'Intelligence Insights',
        hint: 'Project-wide Lens Intelligence analysis. Not limited by Workstream selection.',
        granted: 'Access granted — project-wide across all workstreams.',
    },
    {
        key: 'projectMail',
        title: 'Project Mail',
        hint: 'View project email, retained safe attachments and related evidence. Not limited by Workstream selection.',
        granted: 'Access granted — project-level source collection.',
    },
    {
        key: 'projectMeetings',
        title: 'Meeting Recordings',
        hint: 'View project recordings, transcripts and metadata. Not limited by Workstream selection.',
        granted: 'Access granted — project-level source collection.',
    },
];

export default function AccessGroupForm({
    mode,
    group,
    workstreams,
    existingNames,
    memberCount,
    saving,
    onCancel,
    onSave,
    onEdit,
    onAdvanced,
    adminWorkspace = false,
    projectFeatureKeys = null,
}) {
    const readOnly = mode === 'view';
    const [draft, setDraft] = React.useState(() => {
        if (group) return draftFromGroup(group, workstreams);
        return { ...createEmptyAccessDraft(), workstreamMode: 'all' };
    });

    const patch = value => setDraft(prev => ({ ...prev, ...value }));
    const name = draft.name.trim();
    const duplicate = !adminWorkspace && existingNames.some(item => item.toLowerCase() === name.toLowerCase());
    const hasWorkstreams = adminWorkspace || draft.workstreamMode === 'all' || draft.selectedWorkstreamIds.length > 0;
    const saveDisabled = readOnly || (!adminWorkspace && !name) || duplicate || !hasWorkstreams || saving;
    const allowedProjectKeys = React.useMemo(() => {
        if (adminWorkspace || !Array.isArray(projectFeatureKeys)) return null;
        return new Set(projectFeatureKeys);
    }, [adminWorkspace, projectFeatureKeys]);
    const scopeLabel =
        draft.workstreamMode === 'all'
            ? 'All workstreams'
            : workstreams
                  .filter(ws => draft.selectedWorkstreamIds.includes(ws.id))
                  .map(ws => ws.name)
                  .join(' · ') || 'None selected';
    const selectedPreset = VDR_PRESETS.find(item => item.key === draft.vdrPreset) || VDR_PRESETS[2];

    const toggleFeature = key => {
        if (readOnly || REQUIRED_FEATURE_KEYS.includes(key)) return;
        if (allowedProjectKeys && !allowedProjectKeys.has(key)) return;
        setDraft(prev => {
            const selected = prev.features.includes(key) ? prev.features.filter(item => item !== key) : [...prev.features, key];
            return { ...prev, features: selected };
        });
    };

    const toggleWorkstream = id => {
        setDraft(prev => {
            const selected = prev.selectedWorkstreamIds.includes(id) ? prev.selectedWorkstreamIds.filter(item => item !== id) : [...prev.selectedWorkstreamIds, id];
            return { ...prev, selectedWorkstreamIds: selected };
        });
    };

    const handleSave = () => {
        if (saveDisabled) return;
        onSave({
            ...draft,
            name,
            features: normalizeFeatureKeys(draft.features),
            acls: aclIdsForDraft(draft, workstreams),
        });
    };

    return (
        <div className={styles.panel}>
            <button type="button" className={styles.back} onClick={onCancel}>
                {mode === 'edit' ? '← Cancel' : '← Back'}
            </button>
            <div className={styles.panelHead}>
                <h2 className={styles.title}>
                    {adminWorkspace ? 'Project Administrators' : mode === 'create' ? 'Create access group' : mode === 'edit' ? 'Edit access group' : group?.name}
                </h2>
                {mode !== 'create' && memberCount != null ? <span className={styles.memberCount}>{memberCount}</span> : null}
            </div>

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>{adminWorkspace ? 'Project workspace modules' : 'Workspace access'}</h3>
                <p className={styles.hint}>
                    {adminWorkspace
                        ? 'Choose which Lens modules are enabled for this project. Project administrators always have access to enabled modules. Team access groups can only include a subset.'
                        : 'Choose which parts of Lens this group can use and which workstreams they can access.'}
                </p>
                {!adminWorkspace ? (
                    <>
                        <label className={styles.label} htmlFor="access-group-name">
                            Group name {readOnly ? null : <span aria-hidden="true">*</span>}
                        </label>
                        {readOnly ? (
                            <p className={styles.factValue}>{draft.name}</p>
                        ) : (
                            <input
                                id="access-group-name"
                                className={styles.input}
                                value={draft.name}
                                placeholder="e.g. Technology Advisers"
                                maxLength={100}
                                onChange={event => patch({ name: event.target.value })}
                            />
                        )}
                        {duplicate ? <p className={styles.error}>A group with this name already exists.</p> : null}
                    </>
                ) : null}

                <p className={styles.groupLabel}>{adminWorkspace ? 'Modules' : 'Features'}</p>
                <div className={styles.grid}>
                    {ACCESS_FEATURES.map(feature => {
                        const locked = REQUIRED_FEATURE_KEYS.includes(feature.key);
                        const checked = locked || draft.features.includes(feature.key);
                        const blocked = Boolean(allowedProjectKeys && !allowedProjectKeys.has(feature.key));
                        if (readOnly || locked || blocked) {
                            return (
                                <div key={feature.key} className={styles.check} title={blocked ? 'Enable this module under Project Administrators first.' : undefined}>
                                    <Check checked={checked && !blocked} muted={readOnly || blocked} />
                                    <span>{feature.label}</span>
                                </div>
                            );
                        }
                        return (
                            <button key={feature.key} type="button" className={styles.check} onClick={() => toggleFeature(feature.key)}>
                                <Check checked={checked} />
                                <span>{feature.label}</span>
                            </button>
                        );
                    })}
                </div>

                {!adminWorkspace ? <p className={styles.groupLabel}>Workstreams</p> : null}
                {!adminWorkspace ? (
                    readOnly ? (
                        <p className={styles.factValue}>{scopeLabel}</p>
                    ) : (
                        <div>
                            <button type="button" className={styles.radioRow} onClick={() => patch({ workstreamMode: 'all' })}>
                                <Radio checked={draft.workstreamMode === 'all'} />
                                <span>
                                    All workstreams
                                    {draft.workstreamMode === 'all' ? <span className={styles.radioHint}>Includes new workstreams added to the project later.</span> : null}
                                </span>
                            </button>
                            <button type="button" className={styles.radioRow} onClick={() => patch({ workstreamMode: 'selected' })}>
                                <Radio checked={draft.workstreamMode === 'selected'} />
                                <span>Selected workstreams</span>
                            </button>
                            {draft.workstreamMode === 'selected' ? (
                                <div className={styles.nested}>
                                    {workstreams.map(ws => {
                                        const checked = draft.selectedWorkstreamIds.includes(ws.id);
                                        return (
                                            <button key={ws.id} type="button" className={styles.check} onClick={() => toggleWorkstream(ws.id)}>
                                                <Check checked={checked} />
                                                <span>{ws.name}</span>
                                            </button>
                                        );
                                    })}
                                    <span className={styles.radioHint}>New workstreams added later are not included automatically.</span>
                                </div>
                            ) : null}
                        </div>
                    )
                ) : null}
            </section>

            {!adminWorkspace ? (
                <>
                    <section className={`${styles.section} ${styles.sectionRule}`}>
                        <h3 className={styles.sectionTitle}>Additional project access</h3>
                        <p className={styles.hint}>Grant access to project-wide sources and intelligence. These are not limited by workstream selection.</p>
                        {GRANTS.map(item =>
                            readOnly ? (
                                <div key={item.key} className={styles.grant}>
                                    <div>
                                        <p className={styles.grantTitle}>{item.title}</p>
                                        <p className={draft[item.key] ? `${styles.hint} ${styles.green}` : styles.hint}>{draft[item.key] ? item.granted : 'No access'}</p>
                                    </div>
                                </div>
                            ) : (
                                <div key={item.key} className={styles.grant}>
                                    <div>
                                        <p className={styles.grantTitle}>{item.title}</p>
                                        <p className={styles.hint}>{item.hint}</p>
                                    </div>
                                    <button type="button" className={styles.grantToggle} onClick={() => patch({ [item.key]: !draft[item.key] })}>
                                        <Check checked={Boolean(draft[item.key])} />
                                        <span>Grant access</span>
                                    </button>
                                </div>
                            ),
                        )}
                    </section>

                    <section className={styles.section}>
                        <div className={styles.card}>
                            <p className={styles.grantTitle}>VDR permissions</p>
                            <p className={styles.hint}>These settings apply only to VDR files and folders. They do not affect Information Requests, Observations, Tasks or other Lens features.</p>
                            <div className={styles.scope}>
                                <p className={styles.factLabel}>VDR workstream scope</p>
                                <p className={styles.factValue}>{scopeLabel}</p>
                                <p className={styles.radioHint}>Inherited from workstream access above</p>
                            </div>
                            {readOnly ? (
                                <div className={styles.presetSummary}>
                                    <span className={styles.presetTitle}>{selectedPreset.title}</span>
                                    <ul className={styles.capList}>
                                        {selectedPreset.can
                                            .replace(/^Can:\s*/, '')
                                            .split(' · ')
                                            .map(item => (
                                                <li key={item}>{item}</li>
                                            ))}
                                    </ul>
                                </div>
                            ) : (
                                VDR_PRESETS.map(preset => {
                                    const selected = draft.vdrPreset === preset.key;
                                    return (
                                        <button key={preset.key} type="button" className={`${styles.preset} ${selected ? styles.presetOn : ''}`} onClick={() => patch({ vdrPreset: preset.key })}>
                                            <Radio checked={selected} />
                                            <span>
                                                <span className={styles.presetTitle}>{preset.title}</span>
                                                <span className={styles.radioHint}>{preset.can}</span>
                                                <span className={styles.mapping}>Preset mapping: {preset.mapping}</span>
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                            <div className={styles.section}>
                                <p className={styles.grantTitle}>Advanced VDR permissions</p>
                                <p className={styles.radioHint}>Customise access for specific VDR workstreams, folders or files.</p>
                                {group?.id && onAdvanced ? (
                                    <button type="button" className={styles.textBtn} onClick={onAdvanced}>
                                        Configure advanced VDR permissions →
                                    </button>
                                ) : (
                                    <p className={styles.radioHint}>Folder and file access can be customised after the group is created.</p>
                                )}
                            </div>
                        </div>
                    </section>
                </>
            ) : null}

            <div className={styles.footer}>
                {readOnly ? (
                    <>
                        <button type="button" className={styles.primaryBtn} onClick={onEdit}>
                            {adminWorkspace ? 'Edit project modules' : 'Edit access group'}
                        </button>
                        <button type="button" className={styles.textBtn} onClick={onCancel}>
                            Back
                        </button>
                    </>
                ) : (
                    <>
                        <button type="button" className={styles.primaryBtn} disabled={saveDisabled} onClick={handleSave}>
                            {saving ? 'Saving…' : mode === 'create' ? 'Create access group' : adminWorkspace ? 'Save modules' : 'Save changes'}
                        </button>
                        <button type="button" className={styles.textBtn} onClick={onCancel}>
                            Cancel
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
